import { jsx as _jsx } from "react/jsx-runtime";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { CommandRenderer } from "./CommandRenderer.js";
import { buildApplyRoute, handlePollingCommand, handleNavigationCommandIfApplicable, requiresApplyRoute, } from "./command-handling.js";
import { MessageContext } from "../features/message/MessageContext.js";
import { SubmissionContext } from "../features/form/SubmissionContext.js";
import { OfferActionContext } from "../features/offer/OfferActionContext.js";
import { ConfigContext } from "./runtime-config/ConfigContext.js";
import { useAnalytics } from "../features/analytics/AnalyticsContext.js";
import { makeRuntimeLGController } from "./runtime-controller.js";
/**
 * App-level LG runtime (command handling and form/offer submission).
 * Mount once in `_app` around the page component.
 */
export function LGRuntimeProvider(props) {
    const router = useRouter();
    const [command, setCommand] = useState(undefined);
    const [flashMessage, setFlashMessage] = useState(props.flashMessage);
    // getServerSideProps runs on every client-side navigation and produces a fresh
    // initialCommand object, so the guard is identity-keyed rather than once-only.
    const lastHandledInitial = useRef(undefined);
    const analytics = useAnalytics();
    const controller = useMemo(() => makeRuntimeLGController(props.config ?? {}, props.controllerOptions), 
    // controllerOptions is expected to be a stable module-level object or undefined
    [props.config, props.controllerOptions]);
    useEffect(() => {
        if (props.initialCommand && props.initialCommand !== lastHandledInitial.current) {
            lastHandledInitial.current = props.initialCommand;
            handleCommand(props.initialCommand);
        }
    }, [props.initialCommand]);
    // Mimic the remount semantics of the route-level wrapper: a flash message set
    // during submission survives until the next navigation replaces it
    useEffect(() => {
        setFlashMessage(props.flashMessage);
    }, [props.flashMessage, router.asPath]);
    // TODO: move to LGForm (and/or other components) as prop to handle this from PlasmicStudio
    useEffect(() => {
        if (command?.kind === "await" || command?.kind === "showMessage" || flashMessage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [command, flashMessage]);
    // TODO: refactor to handle flashMsg through whole chain
    const handleCommand = (cmd, context) => {
        if (router.pathname !== "/apply" && requiresApplyRoute(cmd)) {
            // Navigate and keep processing
            const { href, as } = buildApplyRoute(context?.amount);
            void router.push(href, as);
        }
        if (cmd.kind !== "await") {
            handleNavigationCommandIfApplicable(cmd, router).then(() => setCommand(cmd));
            return;
        }
        setCommand(cmd); // show awaiting state immediately
        handlePollingCommand(cmd, controller, router).then((finalCommand) => {
            setCommand(finalCommand); // update to final command after polling completes
        });
    };
    const submitForm = async (payload) => {
        const data = {
            ...(props.initialFormData || {}),
            ...payload.data,
        };
        const result = await controller.submitForm({ ...payload, data });
        if (result.flashMessage) {
            setFlashMessage(result.flashMessage);
        }
        // TODO: we need some reliable way to determine that the form being submitted
        // is related to prospect profile collection and it's the last step.
        // And we should trigger it before (?) we're sending any data to server.
        if (result.command.kind !== "renderForm") {
            const tracked = analytics.profileCollectionFinished(data);
            // Only a redirect unloads the document and cancels the in-flight vendor beacon;
            // the other commands keep the SPA mounted, so don't make the user wait.
            if (result.command.kind === "redirect") {
                await tracked;
            }
        }
        handleCommand(result.command); //TODO: should await here to avoid form blinking?
    };
    const selectOffer = async (payload) => {
        const result = await controller.selectOffer(payload);
        const amount = typeof payload.payload?.amount === "number" ? payload.payload.amount : undefined;
        handleCommand(result.command, { amount });
    };
    const content = command ? (_jsx(CommandRenderer, { command: command, children: props.children })) : (props.children);
    return (_jsx(ConfigContext.Provider, { value: props.config ?? {}, children: _jsx(OfferActionContext.Provider, { value: { selectOffer: selectOffer }, children: _jsx(SubmissionContext.Provider, { value: { submit: submitForm }, children: flashMessage ? (_jsx(MessageContext.Provider, { value: { message: flashMessage }, children: content })) : (content) }) }) }));
}
